import { HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BaseHttpService } from '@core/data/http/base-http.service';
import { CreateContractorRequest } from '@core/models/contractor/create-contractor-request';
import { EditContractorRequest } from '@core/models/contractor/edit-contractor-request';
import { PatchContractorRequest } from '@core/models/contractor/patch-contractor-request';
import { CreateEmployeeRequest } from '@core/models/employee/create-employee-request';
import { EditEmployeeRequest } from '@core/models/employee/edit-employee-request';
import { EmployeeWageUpdateRequest } from '@core/models/employee/employee-wage-update-request';
import { PatchEmployeeRequest } from '@core/models/employee/patch-employee-request';
import { CreateLeavePolicyRequest } from '@core/models/leave/create-leave-policy-request';
import { CreatePayrollRequest } from '@core/models/payroll/create-payroll';
import { ContractorPaymentsReportRequest } from '@core/models/reports/contractor-payments-report-request';
import { PayrollJournalReportRequest } from '@core/models/reports/payroll-journal-report-request';
import { PayrollReportRequest } from '@core/models/reports/payroll-report-request';
import { PayrollSummaryReportRequest } from '@core/models/reports/payroll-summary-report-request';
import { W4ExemptStatusReportRequest } from '@core/models/reports/w4-exempt-status-report-request';
import { environment } from '@environments/environment';
import { Observable } from 'rxjs';
import { EmployeeLeaveBalanceUpdateRequest } from '@core/models/employee/employee-leave-balance-update.request';

@Injectable({ providedIn: 'root' })
export class PayrollDataService extends BaseHttpService {

    private urls = {
        employees: '/payroll/employees',
        employee: '/payroll/employees/:id',
        employeeLeaveBalance: '/payroll/employees/:id/leave/balances/:timeoff_code',
        employeeExclude: '/payroll/employees/:id/action/exclude',
        employeeInvite: '/payroll/employees/:id/onboard/email',
        employeePaystubs: '/payroll/employees/:id/paystubs',
        employeePaystubDownload: '/payroll/employees/:id/paystubs/:paystubId',
        employeeDocuments: '/payroll/employees/:id/documents',
        employeeDocumentDownload: '/payroll/employees/:id/documents/:documentId',
        employeeDeductions: '/payroll/employees/:id/deductions',
        employeeBenefits: '/payroll/employees/:id/benefits',
        employeeBenefitsManagement: '/payroll/component/employee_benefits',
        employeeDeductionsManagement: '/payroll/component/employee_post_tax_deductions',
        employeeRestart: '/payroll/employees/:id/action/rehire',
        employeeTerminate: '/payroll/employees/:id/action/terminate',
        employeeWageType: '/payroll/employees/:id/wages',
        employeeWages: '/payroll/employees/:id/wages',
        employeeWageDeletePending: '/payroll/employees/:id/wages/future',

        // payrolls
        payrolls: '/payroll/payrolls/active',
        createPayroll: '/payroll/payrolls',
        payrollHistory: '/payroll/payrolls/paid',
        payroll: '/payroll/payrolls/:id',
        payrollDetails: '/payroll/component/run_payroll',
        payrollExportJournal: '/payroll/payrolls/:id/action/export_journal',
        payrollSummaryDownload: '/payroll/payrolls/:id/action/download_pay_summary',
        payrollCashRequirementDownload: '/payroll/payrolls/:id/action/download_cash_requirement',
        payrollPaystubsDownload: '/payroll/payrolls/:id/action/export_paystubs',
        deletePayroll: '/payroll/payrolls/:id/action/delete',
        reopenPayroll: '/payroll/payrolls/:id/action/reopen',
        importPayroll: '/payroll/payrolls/:id/action/import_timecards',

        // contractors
        contractors: '/payroll/contractors',
        contractor: '/payroll/contractors/:id',
        contractorInvite: '/payroll/contractors/:id/onboard/email',
        contractorExclude: '/payroll/contractors/:id/action/exclude',
        contractorPayments: '/payroll/contractors/:id/payments',
        contractorPaymentDownload: '/payroll/contractors/:id/payments/:paymentId',
        contractorDocuments: '/payroll/contractors/:id/documents',
        contractorDocumentDownload: '/payroll/contractors/:id/documents/:documentId',
        contractorRestart: '/payroll/contractors/:id/action/rehire',
        contractorTerminate: '/payroll/contractors/:id/action/terminate',

        // leave policies
        leavePolicies: '/payroll/policy/leave',
        leavePolicy: '/payroll/policy/leave/:id',
        leavePolicyMembers: '/payroll/policy/leave/:id/members',

        // reports
        reports: '/payroll/component/company_reports',
        payrollJournalReport: '/payroll/reports/payroll_journal',
        payrollSummaryReport: '/payroll/reports/payroll_summary',
        taxLiabilitiesReport: '/payroll/reports/tax_liabilities',
        benefitsReport: '/payroll/reports/employee_benefits',
        contractorPaymentsReport: '/payroll/reports/contractor_payments',
        childSupportPaymentsReport: '/payroll/reports/child_support_payments',
        w4ExemptStatusReport: '/payroll/reports/w4_exempt_status',

        // taxation
        taxationDocuments: '/payroll/company/tax/documents',
        taxationDeposits: '/payroll/company/tax/deposits',
        taxationDocumentDownload: '/payroll/company/tax/documents/:documentId',

        // company
        company: '/payroll/company',
        companyStatus: '/payroll/company/status',
        onboarding: '/payroll/component/full_service_setup_submission?id=',

        // settings
        settings: '/payroll/company/settings',

        // Workplaces
        workplaces: '/payroll/workplaces',
        workplace: '/payroll/workplaces/:id',

        // leave balances
        leaveBalances: '/payroll/leave/balances',
    };

    fetchEmployees(queryParams?: any): Observable<any> {
        let url: string = environment.api.endpointBase + this.urls.employees;

        if (queryParams) {
            url = `${url}${this.toQueryParams(queryParams)}`;
        }

        return this.get(url, undefined, true);
    }

    fetchEmployee(id: string): Observable<any> {
        const url: string = environment.api.endpointBase + this.urls.employee.replace(':id', id);
        return this.get(url, undefined, true);
    }

    sendEmployeeInvitation(id: string): Observable<any> {
        const url: string = environment.api.endpointBase + this.urls.employeeInvite.replace(':id', id);
        return this.post(url, undefined, undefined, true);
    }

    fetchEmployeePaystubs(id: string, cursor: string = '', perPage?: number): Observable<any> {
        let requestUrl: string = environment.api.endpointBase + this.urls.employeePaystubs.replace(':id', id);

        requestUrl = `${requestUrl}?cursor=${cursor}`;
        if (perPage) {
            requestUrl += `&limit=${perPage}`;
        }

        return this.get(requestUrl, undefined, true);
    }

    downloadEmployeePaystub(id: string, paystubId: string): Observable<any> {
        const url: string = environment.api.endpointBase + this.urls.employeePaystubDownload.replace(':id', id).replace(':paystubId', paystubId);

        const options = {
            headers: new HttpHeaders().set('Accept', 'application/pdf'),
            responseType: 'arraybuffer',
        };

        return this.get(url, options, true);
    }

    fetchEmployeeDocuments(id: string, cursor: string = '', perPage?: number): Observable<any> {
        let requestUrl: string = environment.api.endpointBase + this.urls.employeeDocuments.replace(':id', id);

        requestUrl = `${requestUrl}?cursor=${cursor}`;
        if (perPage) {
            requestUrl += `&limit=${perPage}`;
        }

        return this.get(requestUrl, undefined, true);
    }

    downloadEmployeeDocument(id: string, documentId: string): Observable<any> {
        const url: string = environment.api.endpointBase + this.urls.employeeDocumentDownload.replace(':id', id).replace(':documentId', documentId);

        const options = {
            headers: new HttpHeaders().set('Accept', 'application/pdf'),
            responseType: 'arraybuffer',
        };

        return this.get(url, options, true);
    }

    fetchEmployeeDeductions(id: string, cursor: string = '', perPage?: number): Observable<any> {
        let requestUrl: string = environment.api.endpointBase + this.urls.employeeDeductions.replace(':id', id);

        requestUrl = `${requestUrl}?cursor=${cursor}`;
        if (perPage) {
            requestUrl += `&limit=${perPage}`;
        }

        return this.get(requestUrl, undefined, true);
    }

    fetchEmployeeBenefits(id: string, cursor: string = '', perPage?: number): Observable<any> {
        let requestUrl: string = environment.api.endpointBase + this.urls.employeeBenefits.replace(':id', id);

        requestUrl = `${requestUrl}?cursor=${cursor}`;
        if (perPage) {
            requestUrl += `&limit=${perPage}`;
        }

        return this.get(requestUrl, undefined, true);
    }

    fetchBenefitsManagementUrl(employeeId: string): Observable<any> {
        const url: string = environment.api.endpointBase + this.urls.employeeBenefitsManagement + this.toQueryParams({ id: employeeId});
        return this.get(url, undefined, true);
    }

    fetchEmployeeWages(id: string): Observable<any> {
        const url: string = environment.api.endpointBase + this.urls.employeeWages.replace(':id', id);
        return this.get(url, undefined, true);
    }

    fetchDeductionsManagementUrl(employeeId: string): Observable<any> {
        const url: string = environment.api.endpointBase + this.urls.employeeDeductionsManagement + this.toQueryParams({ id: employeeId});
        return this.get(url, undefined, true);
    }

    createEmployee(employee: CreateEmployeeRequest): Observable<any> {
        const url: string = environment.api.endpointBase + this.urls.employees;
        return this.post(url, employee, undefined, true);
    }

    updateEmployee(id: string, employee: EditEmployeeRequest): Observable<any> {
        const url: string = environment.api.endpointBase + this.urls.employee.replace(':id', id);
        return this.put(url, employee, undefined, true);
    }

    patchEmployee(id: string, employee: PatchEmployeeRequest): Observable<any> {
        const url: string = environment.api.endpointBase + this.urls.employee.replace(':id', id);
        return this.patch(url, employee, undefined, true);
    }

    patchEmployees(employees: PatchEmployeeRequest[]): Observable<any> {
        const url: string = environment.api.endpointBase + this.urls.employees;
        return this.patch(url, { employees }, undefined, true);
    }

    excludeEmployee(id: string): Observable<any> {
        const url: string = environment.api.endpointBase + this.urls.employeeExclude.replace(':id', id);
        return this.post(url, undefined, undefined, true);
    }

    restartEmployee(id: string, startDate: string): Observable<any> {
        const url: string = environment.api.endpointBase + this.urls.employeeRestart.replace(':id', id);
        return this.post(url, { start_date: startDate }, undefined, true);
    }

    terminateEmployee(id: string, terminationDate: string): Observable<any> {
        const url: string = environment.api.endpointBase + this.urls.employeeTerminate.replace(':id', id);
        return this.post(url, { termination_date: terminationDate }, undefined, true);
    }

    updateEmployeeWage(id: string, request: EmployeeWageUpdateRequest): Observable<any> {
        const url: string = environment.api.endpointBase + this.urls.employeeWageType.replace(':id', id);
        return this.post(url, request, undefined, true);
    }

    deletePendingEmployeeWage(id: string): Observable<any> {
        const url: string = environment.api.endpointBase + this.urls.employeeWageDeletePending.replace(':id', id);
        return this.delete(url, undefined, undefined, true);
    }

    updateEmployeeLeaveBalance(id: string, timeoffCode: string, request: EmployeeLeaveBalanceUpdateRequest): Observable<any> {
        const url: string = environment.api.endpointBase + this.urls.employeeLeaveBalance.replace(':id', id).replace(':timeoff_code', timeoffCode);
        return this.post(url, request, undefined, true);
    }
    // payrolls
    createPayroll(payroll: CreatePayrollRequest): Observable<any> {
        const url: string = environment.api.endpointBase + this.urls.createPayroll;
        return this.post(url, payroll, undefined, true);
    }

    deletePayroll(id: string): Observable<any> {
        const url: string = environment.api.endpointBase + this.urls.deletePayroll.replace(':id', id);
        return this.post(url, undefined,undefined, true);
    }

    reopenPayroll(id: string): Observable<any> {
        const url: string = environment.api.endpointBase + this.urls.reopenPayroll.replace(':id', id);
        return this.post(url, undefined, undefined, true);
    }

    importPayroll(id: string): Observable<any> {
        const url: string = environment.api.endpointBase + this.urls.importPayroll.replace(':id', id);
        return this.post(url, undefined, undefined, true);
    }

    fetchPayrolls(queryParams?: any): Observable<any> {
        let url: string = environment.api.endpointBase + this.urls.payrolls;

        if (queryParams) {
            url = `${url}${this.toQueryParams(queryParams)}`;
        }

        return this.get(url, undefined, true);
    }

    fetchPayroll(id: string): Observable<any> {
        const url: string = environment.api.endpointBase + this.urls.payroll.replace(':id', id);
        return this.get(url, undefined, true);
    }

    fetchPayrollDetailsUrl(payrollId: string): Observable<any> {
        const url: string = environment.api.endpointBase + this.urls.payrollDetails + this.toQueryParams({ id: payrollId });
        return this.get(url, undefined, true);
    }

    fetchPayrollHistory(queryParams?: any): Observable<any> {
        let url: string = environment.api.endpointBase + this.urls.payrollHistory;

        if (queryParams) {
            url = `${url}${this.toQueryParams(queryParams)}`;
        }

        return this.get(url, undefined, true);
    }

    exportPayrollJournal(payrollId: string): Observable<any> {
        const url: string = environment.api.endpointBase + this.urls.payrollExportJournal.replace(':id', payrollId);
        
        const options = {
            headers: new HttpHeaders().set('Accept', 'application/json, text/plain, */*'),
            responseType: 'arraybuffer',
        };

        return this.post(url, undefined, options, true);
    }

    downloadPaySummary(payrollId: string): Observable<any> {
        const url: string = environment.api.endpointBase + this.urls.payrollSummaryDownload.replace(':id', payrollId);

        const options = {
            headers: new HttpHeaders().set('Accept', 'application/json, text/plain, */*'),
            responseType: 'arraybuffer',
        };

        return this.post(url, undefined, options, true);
    }

    downloadCashRequirement(payrollId: string): Observable<any> {
        const url: string = environment.api.endpointBase + this.urls.payrollCashRequirementDownload.replace(':id', payrollId);

        const options = {
            headers: new HttpHeaders().set('Accept', 'application/json, text/plain, */*'),
            responseType: 'arraybuffer',
        };

        return this.post(url, undefined, options, true);
    }

    downloadPaystubs(payrollId: string, timeout?: number, async_id?: string): Observable<any> {
        let url: string = environment.api.endpointBase + this.urls.payrollPaystubsDownload.replace(':id', payrollId);

        const params: any = {};

        if (timeout) {
            params.timeout = timeout;
        }

        if (async_id) {
            params.async_id = async_id;
        }

        if (Object.keys(params).length > 0) {
            url = `${url}${this.toQueryParams(params)}`;
        }

        const options = {
            headers: new HttpHeaders().set('Accept', 'application/json, text/plain, */*'),
            responseType: 'arraybuffer',
            observe: 'response'
        };

        return this.post(url, undefined, options, true);
    }

    // Contractors
    fetchContractors(queryParams?: any): Observable<any> {
        let url: string = environment.api.endpointBase + this.urls.contractors;

        if (queryParams) {
            url = `${url}${this.toQueryParams(queryParams)}`;
        }

        return this.get(url, undefined, true);
    }

    fetchContractor(id: string): Observable<any> {
        const url: string = environment.api.endpointBase + this.urls.contractor.replace(':id', id);
        return this.get(url, undefined, true);
    }

    fetchContractorPayments(id: string, cursor: string = '', perPage?: number): Observable<any> {
        let requestUrl: string = environment.api.endpointBase + this.urls.contractorPayments.replace(':id', id);

        requestUrl = `${requestUrl}?cursor=${cursor}`;
        if (perPage) {
            requestUrl += `&limit=${perPage}`;
        }

        return this.get(requestUrl, undefined, true);
    }

    downloadContractorPayment(id: string, paymentId: string): Observable<any> {
        const url: string = environment.api.endpointBase + this.urls.contractorPaymentDownload.replace(':id', id).replace(':paymentId', paymentId);

        const options = {
            headers: new HttpHeaders().set('Accept', 'application/pdf'),
            responseType: 'arraybuffer',
        };

        return this.get(url, options, true);
    }

    fetchContractorDocuments(id: string, cursor: string = '', perPage?: number): Observable<any> {
        let requestUrl: string = environment.api.endpointBase + this.urls.contractorDocuments.replace(':id', id);

        requestUrl = `${requestUrl}?cursor=${cursor}`;
        if (perPage) {
            requestUrl += `&limit=${perPage}`;
        }

        return this.get(requestUrl, undefined, true);
    }

    downloadContractorDocument(id: string, documentId: string): Observable<any> {
        const url: string = environment.api.endpointBase + this.urls.contractorDocumentDownload.replace(':id', id).replace(':documentId', documentId);

        const options = {
            headers: new HttpHeaders().set('Accept', 'application/pdf'),
            responseType: 'arraybuffer',
        };

        return this.get(url, options, true);
    }

    createContractor(contractor: CreateContractorRequest): Observable<any> {
        const url: string = environment.api.endpointBase + this.urls.contractors;
        return this.post(url, contractor, undefined, true);
    }

    updateContractor(id: string, contractor: EditContractorRequest): Observable<any> {
        const url: string = environment.api.endpointBase + this.urls.contractor.replace(':id', id);
        return this.put(url, contractor, undefined, true);
    }

    patchContractor(id: string, contractor: PatchContractorRequest): Observable<any> {
        const url: string = environment.api.endpointBase + this.urls.contractor.replace(':id', id);
        return this.patch(url, contractor, undefined, true);
    }

    excludeContractor(id: string): Observable<any> {
        const url: string = environment.api.endpointBase + this.urls.contractorExclude.replace(':id', id);
        return this.post(url, undefined, undefined, true);
    }

    restartContractor(id: string, startDate: string): Observable<any> {
        const url: string = environment.api.endpointBase + this.urls.contractorRestart.replace(':id', id);
        return this.post(url, { start_date: startDate }, undefined, true);
    }

    terminateContractor(id: string, terminationDate: string): Observable<any> {
        const url: string = environment.api.endpointBase + this.urls.contractorTerminate.replace(':id', id);
        return this.post(url, { termination_date: terminationDate }, undefined, true);
    }

    sendContractorInvitation(id: string): Observable<any> {
        const url: string = environment.api.endpointBase + this.urls.contractorInvite.replace(':id', id);
        return this.post(url, undefined, undefined, true);
    }

    // Reports
    fetchRunReportstUrl(): Observable<any> {
        const url: string = environment.api.endpointBase + this.urls.reports;
        return this.get(url, undefined, true);
    }

    // Taxation
    fetchTaxationDocuments(queryParams?: any): Observable<any> {
        let url: string = environment.api.endpointBase + this.urls.taxationDocuments;

        if (queryParams) {
            url = `${url}${this.toQueryParams(queryParams)}`;
        }

        return this.get(url, undefined, true);
    }

    fetchTaxationDeposits(queryParams?: any): Observable<any> {
        let url: string = environment.api.endpointBase + this.urls.taxationDeposits;

        if (queryParams) {
            url = `${url}${this.toQueryParams(queryParams)}`;
        }

        return this.get(url, undefined, true);
    }

    // company
    fetchCompany(): Observable<any> {
        const url: string = environment.api.endpointBase + this.urls.company;
        return this.get(url, undefined, true);
    }

    fetchCompanyStatus(): Observable<any> {
        const url: string = environment.api.endpointBase + this.urls.companyStatus;
        return this.get(url, undefined, true);
    }

    fetchOnboardingUrl(checkCompanyId: string): Observable<any> {
        const url: string = environment.api.endpointBase + this.urls.onboarding + checkCompanyId;
        return this.get(url, undefined, true);
    }

    downloadTaxationDocument(documentId: string): Observable<any> {
        const url: string = environment.api.endpointBase + this.urls.taxationDocumentDownload.replace(':documentId', documentId);

        const options = {
            headers: new HttpHeaders().set('Accept', 'application/pdf'),
            responseType: 'arraybuffer',
        };

        return this.get(url, options, true);
    }

    // settings
    fetchSettings(): Observable<any> {
        const url: string = environment.api.endpointBase + this.urls.settings;
        return this.get(url, undefined, true);
    }

    updateSettings(settings: any): Observable<any> {
        const url: string = environment.api.endpointBase + this.urls.settings;
        return this.patch(url, settings, undefined, true);
    }

    // Workplaces
    fetchWorkplaces(queryParams?: any): Observable<any> {
        let url: string = environment.api.endpointBase + this.urls.workplaces;

        if (queryParams) {
            url = `${url}${this.toQueryParams(queryParams)}`;
        }

        return this.get(url, undefined, true);
    }

    fetchWorkplace(id: string): Observable<any> {
        const url: string = environment.api.endpointBase + this.urls.workplace.replace(':id', id);
        return this.get(url, undefined, true);
    }

    // Leave Policies
    fetchLeavePolicies(): Observable<any> {
        const url: string = environment.api.endpointBase + this.urls.leavePolicies;
        return this.get(url, undefined, true);
    }

    fetchLeavePolicy(id: string): Observable<any> {
        const url: string = environment.api.endpointBase + this.urls.leavePolicy.replace(':id', id);
        return this.get(url, undefined, true);
    }

    createLeavePolicy(request: CreateLeavePolicyRequest): Observable<any> {
        const url: string = environment.api.endpointBase + this.urls.leavePolicies;
        return this.post(url, request, undefined, true);
    }

    updateLeavePolicyEmployees(id: string, request: any): Observable<any> {
        const url: string = environment.api.endpointBase + this.urls.leavePolicyMembers.replace(':id', id);
        return this.put(url, request, undefined, true);
    }

    // Reports
    downloadPayrollJournalReport(request: PayrollJournalReportRequest): Observable<any> {
        const url: string = environment.api.endpointBase + this.urls.payrollJournalReport;

        const options = {
            headers: new HttpHeaders().set('Accept', 'text/csv, */*'),
            responseType: 'arraybuffer',
        };

        return this.post(url, request, options, true);
    }

    downloadPayrollSummaryReport(request: PayrollSummaryReportRequest): Observable<any> {
        const url: string = environment.api.endpointBase + this.urls.payrollSummaryReport;

        const options = {
            headers: new HttpHeaders().set('Accept', 'text/csv, */*'),
            responseType: 'arraybuffer',
        };

        return this.post(url, request, options, true);
    }

    downloadTaxLiabilitiesReport(request: PayrollReportRequest): Observable<any> {
        const url: string = environment.api.endpointBase + this.urls.taxLiabilitiesReport;

        const options = {
            headers: new HttpHeaders().set('Accept', 'text/csv, */*'),
            responseType: 'arraybuffer',
        };

        return this.post(url, request, options, true);
    }

    downloadBenefitsReport(request: PayrollReportRequest): Observable<any> {
        const url: string = environment.api.endpointBase + this.urls.benefitsReport;

        const options = {
            headers: new HttpHeaders().set('Accept', 'text/csv, */*'),
            responseType: 'arraybuffer',
        };

        return this.post(url, request, options, true);
    }

    downloadContractorPaymentsReport(request: ContractorPaymentsReportRequest): Observable<any> {
        const url: string = environment.api.endpointBase + this.urls.contractorPaymentsReport;

        const options = {
            headers: new HttpHeaders().set('Accept', 'text/csv, */*'),
            responseType: 'arraybuffer',
        };

        return this.post(url, request, options, true);
    }

    downloadChildSupportPaymentsReport(request: PayrollReportRequest): Observable<any> {
        const url: string = environment.api.endpointBase + this.urls.childSupportPaymentsReport;

        const options = {
            headers: new HttpHeaders().set('Accept', 'text/csv, */*'),
            responseType: 'arraybuffer',
        };

        return this.post(url, request, options, true);
    }

    downloadW4ExemptStatusReport(request: W4ExemptStatusReportRequest): Observable<any> {
        const url: string = environment.api.endpointBase + this.urls.w4ExemptStatusReport;

        const options = {
            headers: new HttpHeaders().set('Accept', 'text/csv, */*'),
            responseType: 'arraybuffer',
        };

        return this.post(url, request, options, true);
    }

    downloadLeaveBalancesReport(): Observable<any> {
        const url: string = environment.api.endpointBase + this.urls.leaveBalances;

        const options = {
            headers: new HttpHeaders().set('Accept', 'text/csv, */*'),
            responseType: 'arraybuffer',
        };

        return this.get(url, options, true);
    }
}
